import { Link } from '@tanstack/react-router';
import { SEO } from '@/components';

export function TermsPage() {
  return (
    <div className="max-w-3xl mx-auto">
      <SEO
        title="Terms of Use"
        description="Terms of use for miweather.org. Read our service agreement and policies for using our Michigan weather application."
        path="/terms"
      />
      <Link to="/" className="text-[var(--color-info)] hover:underline text-sm mb-4 inline-block">
        ← Back to Home
      </Link>

      <h1 className="text-3xl font-bold mb-6">Terms of Use</h1>

      <div className="space-y-6 text-white/80">
        <p className="text-white/60 text-sm">Last updated: January 2025</p>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Acceptance of Terms</h2>
          <p>
            By accessing and using miweather.org, you accept and agree to be bound by these Terms
            of Use and our{' '}
            <Link to="/privacy" className="text-[var(--color-info)] hover:underline">
              Privacy Policy
            </Link>
            , which is incorporated herein by reference. If you do not agree to these terms, please
            do not use this service.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Eligibility</h2>
          <p>
            You must be at least 18 years of age to use this service. By using miweather.org, you
            represent and warrant that you are at least 18 years old or have reached the age of
            majority in your jurisdiction. If you are under 18, please do not use this service.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Description of Service</h2>
          <p>
            miweather.org is a free weather information service focused on Michigan. We aggregate
            and display weather data from public sources, primarily the National Weather Service
            (NWS), to provide forecasts, alerts, and current conditions for Michigan locations.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Weather Data Disclaimer</h2>
          <p className="mb-2">
            <strong className="text-white">Weather forecasts are inherently uncertain.</strong> While
            we strive to provide accurate information, you acknowledge that:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Weather forecasts are predictions and may not be accurate</li>
            <li>Conditions can change rapidly and without warning</li>
            <li>You should not rely solely on this service for safety-critical decisions</li>
            <li>
              For severe weather emergencies, always follow official guidance from the National
              Weather Service and local authorities
            </li>
          </ul>
          <p className="mt-4 mb-2">
            <strong className="text-white">
              We are not responsible for decisions you make based on our forecasts.
            </strong>{' '}
            This includes, but is not limited to:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Cancelled or rescheduled outdoor events, travel plans, or activities</li>
            <li>Property damage from weather you were or were not warned about</li>
            <li>Travel delays, accidents, or road conditions</li>
            <li>Business losses or missed opportunities</li>
            <li>Personal injury or health-related decisions</li>
            <li>Agricultural, construction, or other professional planning decisions</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">No Warranty</h2>
          <p>
            This service is provided "as is" and "as available" without warranties of any kind,
            either express or implied. We do not guarantee that the service will be uninterrupted,
            timely, secure, or error-free. We make no warranties regarding the accuracy,
            reliability, or completeness of any weather data or forecasts.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Limitation of Liability</h2>
          <p className="mb-2">
            To the fullest extent permitted by law, miweather.org and its operators shall not be
            liable for any direct, indirect, incidental, special, consequential, or punitive
            damages arising from your use of or inability to use this service, including but not
            limited to damages resulting from reliance on weather information provided.
          </p>
          <p>
            In no event shall our total liability to you for all damages, losses, or causes of
            action exceed one hundred U.S. dollars ($100.00 USD). This limitation applies regardless
            of the form of action, whether in contract, tort (including negligence), strict
            liability, or otherwise.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless miweather.org and its operators,
            affiliates, officers, and employees from and against any claims, liabilities, damages,
            losses, costs, or expenses (including reasonable attorneys' fees) arising out of or
            related to your use of the service, your violation of these Terms of Use, or your
            violation of any rights of another party.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Data Sources</h2>
          <p className="mb-2">Weather data is sourced from:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>National Weather Service (NWS):</strong> Forecasts, alerts, and current
              conditions. NWS data is in the public domain.
            </li>
            <li>
              <strong>Open-Meteo:</strong> Supplementary weather data for enhanced forecasts.
            </li>
          </ul>
          <p className="mt-2">
            We are not affiliated with or endorsed by these organizations.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Intellectual Property</h2>
          <p>
            The miweather.org website, including its design, layout, graphics, user interface, and
            underlying code, is owned by miweather.org and protected by copyright and other
            intellectual property laws. Weather data sourced from the National Weather Service is
            in the public domain. You may not copy, reproduce, distribute, or create derivative
            works from our website design or code without prior written permission.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Acceptable Use</h2>
          <p className="mb-2">You agree not to:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Use automated systems to excessively access the service</li>
            <li>Attempt to interfere with or disrupt the service</li>
            <li>Misrepresent the source of weather data obtained from this service</li>
            <li>Use the service for any unlawful purpose</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Service Availability</h2>
          <p>
            We reserve the right to modify, suspend, or discontinue the service at any time without
            notice. We are not liable for any modification, suspension, or discontinuation of the
            service.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Transmission Failures</h2>
          <p>
            We will use commercially reasonable efforts to provide weather data on a continuous and
            timely basis. However, we are not responsible for any failure or malfunction in
            communications, any failure of third-party data providers (including the National
            Weather Service) to transmit data, or any failure to transmit weather information
            resulting from natural disasters, severe weather events, wars, labor strikes, power
            outages, internet service disruptions, government actions, or other events beyond our
            reasonable control. You acknowledge that during severe weather events, the very
            conditions you need information about may prevent us from delivering that information.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Viruses and Security</h2>
          <p>
            We will use commercially reasonable efforts to maintain the site free of viruses and
            other harmful components. However, we cannot guarantee that the site, its servers, or
            any data downloaded from the site is free from viruses, malware, or other harmful
            components. You are responsible for implementing sufficient security measures (such as
            anti-virus software) on your own devices. We are not liable for any damage to your
            computer equipment, mobile device, or other property resulting from your access to or
            use of this service.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Governing Law</h2>
          <p>
            These Terms of Use shall be governed by and construed in accordance with the laws of
            the State of Montana, without regard to its conflict of law provisions. Any disputes
            arising under or in connection with these terms shall be subject to the exclusive
            jurisdiction of the state and federal courts located in Montana.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Dispute Resolution and Class Action Waiver</h2>
          <p className="mb-2">
            Any dispute, claim, or controversy arising out of or relating to these Terms of Use or
            your use of the service shall be resolved on an individual basis. You agree to waive
            any right to participate in a class action lawsuit or class-wide arbitration against
            miweather.org or its operators.
          </p>
          <p>
            By using this service, you agree that any disputes will be resolved through individual
            legal proceedings and not as part of any class, consolidated, or representative action.
            This waiver applies to the fullest extent permitted by applicable law.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Changes to Terms</h2>
          <p>
            We may update these Terms of Use from time to time. Continued use of the service after
            changes constitutes acceptance of the new terms. We encourage you to review this page
            periodically.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Severability</h2>
          <p>
            If any provision of these Terms of Use is found to be invalid, illegal, or
            unenforceable by a court of competent jurisdiction, such invalidity shall not affect
            the validity of the remaining provisions, which shall remain in full force and effect.
            The invalid or unenforceable provision shall be modified to the minimum extent
            necessary to make it valid and enforceable while preserving the parties' original
            intent.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Entire Agreement</h2>
          <p>
            These Terms of Use, together with our Privacy Policy, constitute the entire agreement
            between you and miweather.org regarding your use of the service and supersede all prior
            agreements, understandings, and communications, whether written or oral. Any waiver of
            any provision of these Terms shall not be effective unless in writing and signed by
            miweather.org.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-2 text-white">Contact</h2>
          <p>
            For questions about these terms, please contact us at{' '}
            <a
              href="mailto:legal@miweather.org"
              className="text-[var(--color-info)] hover:underline"
            >
              legal@miweather.org
            </a>
            .
          </p>
        </section>

        <section className="pt-4 border-t border-white/10">
          <p className="text-white/60">
            See also: <Link to="/privacy" className="text-[var(--color-info)] hover:underline">Privacy Policy</Link>
          </p>
        </section>
      </div>
    </div>
  );
}
